import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {KeycloakService} from 'keycloak-angular';
import {LookupService} from '../../../../common/services/lookup.service';
import {HelpService} from '../../../../other/services/help.service';
import {ProviderPriceListLinkDTO} from '../../../models/PriceListDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {HelpDTO} from '../../../../other/models/HelpDTO';
import {debounceTime} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {Common, setPotentialNullDateValueForFormControl} from '../../../../common/util/Util';
import {CodingSchemaService} from '../../../services/coding-schema.service';
import {CodingSchemaDTO} from '../../../models/CodingSchemaDTO';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';

@Component({
  selector: 'app-add-price-dialog',
  templateUrl: './add-price-dialog.component.html',
  styleUrls: ['./add-price-dialog.component.scss', '../../../../../../styles/global/_dialog-styles.scss']
})
export class AddPriceDialogComponent implements OnInit {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly lookupValue = LookupValue;

  priceFormGroup: FormGroup;
  codingSchemaTypeFormControl: FormControl;
  codingSchemaFormControl: FormControl;
  proprietaryCodeFormControl: FormControl;
  proprietaryDescriptionFormControl: FormControl;
  grossAmountFormControl: FormControl;
  discountAmountFormControl: FormControl;
  currencyFormControl: FormControl;
  startDateFormControl: FormControl;
  endDateFormControl: FormControl;

  codingSchemaTextChanged = new Subject<string>();
  codingSchemaSubscription: Subscription;
  selectedCodingSchema: CodingSchemaDTO;

  currencies: NameValueDTO[];
  codingSchemaTypes: NameValueDTO[];
  helpInfo: HelpDTO[];
  filteredCodingSchemas: CodingSchemaDTO[];

  isEdit = false;
  userFirstLastName = '';
  errorMessage = '';
  heading = 'Add Price';
  rowIndex = -1;
  priceDTO: ProviderPriceListLinkDTO;

  constructor(
    public dialogRef: MatDialogRef<AddPriceDialogComponent>,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private keycloakService: KeycloakService,
    private lookupService: LookupService,
    private helpService: HelpService,
    private codingSchemaService: CodingSchemaService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    const price = data.price;
    const index = data.index;
    this.priceDTO = price;
    if (price.codingSchema != null) {
      this.rowIndex = index;
      this.isEdit = true;
      this.heading = 'Edit Price';
      this.selectedCodingSchema = price.codingSchema;
    }
    this.codingSchemaTypeFormControl = new FormControl(this.isEdit && this.priceDTO.codingSchema != null ? this.priceDTO.codingSchema.codingSchemaType.name : '', Validators.required);
    this.codingSchemaFormControl = new FormControl(this.isEdit ? this.getCodingSchemaDisplayString(this.selectedCodingSchema) : '', Validators.required);
    this.proprietaryCodeFormControl = new FormControl(this.isEdit ? this.priceDTO.proprietaryCode : '');
    this.proprietaryDescriptionFormControl = new FormControl(this.isEdit ? this.priceDTO.proprietaryDescription : '');
    this.grossAmountFormControl = new FormControl(this.isEdit ? this.priceDTO.grossAmount : 0.00, Validators.required);
    this.discountAmountFormControl = new FormControl(this.isEdit ? this.priceDTO.discountAmount : 0.00, Validators.required);
    this.currencyFormControl = new FormControl(this.isEdit && this.priceDTO.currency != null ? this.priceDTO.currency.name : '', Validators.required);
    this.startDateFormControl = new FormControl(this.isEdit ? new Date(this.priceDTO.startDate) : '', Validators.required);
    this.endDateFormControl = new FormControl(this.isEdit ? setPotentialNullDateValueForFormControl(this.priceDTO.endDate) : null);

    this.priceFormGroup = this.formBuilder.group({
      codingSchemaType: this.codingSchemaTypeFormControl,
      codingSchema: this.codingSchemaFormControl,
      proprietaryCode: this.proprietaryCodeFormControl,
      proprietaryDescription: this.proprietaryDescriptionFormControl,
      grossAmount: this.grossAmountFormControl,
      discountAmount: this.discountAmountFormControl,
      currency: this.currencyFormControl,
      startDate: this.startDateFormControl,
      endDate: this.endDateFormControl
    });

    this.codingSchemaSubscription = this.codingSchemaTextChanged.pipe(
      debounceTime(this.debounceTime)
    ).subscribe(() => {
      this.getFilteredListOfCodingSchemas();
    });

    this.heading = `${this.heading} for ${this.priceDTO.provider != null ? this.priceDTO.provider.name : this.priceDTO.providerNetwork.name}`;
  }

  ngOnInit(): void {
    this.getListOfLookupValues(this.lookupValue.Currency);
    this.getListOfLookupValues(this.lookupValue.CodingSchemaType);
    this.getHelpInfo('Provider Price List');

    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.Currency) {
          this.currencies = response;
        }
        else if (entity === this.lookupValue.CodingSchemaType) {
          this.codingSchemaTypes = response;
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName).subscribe(
      (response) => {
        this.helpInfo = response;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getFilteredListOfCodingSchemas(): void {
    const codingSchemaType: NameValueDTO = this.codingSchemaTypes.find(type => type.name === this.codingSchemaTypeFormControl.value);
    this.codingSchemaService.getListOfCodingSchemasByDescriptionAndType(this.codingSchemaFormControl.value, codingSchemaType.value).subscribe(
      (response) => {
        this.filteredCodingSchemas = response.resultList;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.openSnackBar(error.businessMessage, 'Dismiss');
      }
    );
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo != null) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo != null ? helpInfo.inputInfo : '';
    }
    return message;
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  onSaveClick(): void {
    if (this.priceFormGroup.valid) {
      const newPriceDTO = new ProviderPriceListLinkDTO();
      newPriceDTO.provider = this.priceDTO.provider;
      newPriceDTO.providerNetwork = this.priceDTO.providerNetwork;
      newPriceDTO.codingSchema = this.selectedCodingSchema;
      newPriceDTO.proprietaryCode = this.proprietaryCodeFormControl.value;
      newPriceDTO.proprietaryDescription = this.proprietaryDescriptionFormControl.value;
      newPriceDTO.currency = this.currencies.find(cur => cur.name === this.currencyFormControl.value);
      newPriceDTO.grossAmount = this.grossAmountFormControl.value;
      newPriceDTO.discountAmount = this.discountAmountFormControl.value;
      newPriceDTO.netAmount = newPriceDTO.grossAmount - newPriceDTO.discountAmount;
      newPriceDTO.startDate = this.startDateFormControl.value;
      newPriceDTO.endDate = this.endDateFormControl.value;

      if (this.isEdit) {
        newPriceDTO.created = this.priceDTO.created;
        newPriceDTO.createdBy = this.priceDTO.createdBy;
        newPriceDTO.updated = new Date();
        newPriceDTO.updatedBy = this.userFirstLastName;
      }
      else {
        newPriceDTO.created = new Date();
        newPriceDTO.createdBy = this.userFirstLastName;
      }

      this.dialogRef.close({price: newPriceDTO, index: this.rowIndex});
      this.priceFormGroup.reset();
    }
    else {
      this.errorMessage = 'Please complete all required fields.';
      this.openSnackBar('Please complete all required fields.', 'Dismiss');
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  getCodingSchemaDisplayString(codingSchema: CodingSchemaDTO): string {
    return `${codingSchema.code} - ${codingSchema.shortDescription}`;
  }

  onInputChangedSearchCodingSchema($event) {
    if ($event.target != null) {
      this.codingSchemaTextChanged.next($event.target.value);
    }
  }

  codingSchemaSelected(name: string) {
    this.selectedCodingSchema = this.filteredCodingSchemas.find(schema => name.includes(schema.code));
  }

  clearCodingSchemaDescription(codingSchemaFormControl: FormControl) {
    codingSchemaFormControl.reset();
    this.filteredCodingSchemas = [];
  }

  onDiscountAmountValue() {
    const discountValue = Number(this.discountAmountFormControl.value);
    this.discountAmountFormControl.setValue(discountValue.toFixed(2));
  }

  onGrossAmountValue() {
    const grossValue = Number(this.grossAmountFormControl.value);
    this.grossAmountFormControl.setValue(grossValue.toFixed(2));
  }
}
